
import { Question } from "../../types/assessment";
import { createGenericOptions } from "../utils";

export const strategyQuestions: Question[] = [
  {
    id: "s1",
    text: "Does your organization have a formal AI strategy?",
    dimension: "strategy",
    tooltip: "Consider whether your organization has documented AI objectives, roadmap, and implementation plans. Look for formal strategy documents, board presentations, or explicit integration of AI into corporate strategic plans.",
    options: [
      {
        value: 0,
        label: "No formal AI strategy exists",
        description: "The organization has not developed any formal AI strategy. AI initiatives, if any, are sporadic and not tied to business objectives or documented plans."
      },
      {
        value: 1,
        label: "Early discussions but no documented strategy",
        description: "Leadership has begun exploring AI potential through informal discussions, but no documentation exists. Ideas remain conceptual without concrete plans."
      },
      {
        value: 2,
        label: "Draft strategy in development",
        description: "A preliminary AI strategy document is being created with basic objectives identified. Still requires refinement, approval, and implementation planning."
      },
      {
        value: 3,
        label: "Defined strategy with basic implementation plan",
        description: "Formal AI strategy has been approved with identified priorities and a basic roadmap. Initial implementation steps are documented but lack comprehensive details."
      },
      {
        value: 4,
        label: "Comprehensive strategy with detailed roadmap",
        description: "Well-developed AI strategy with detailed implementation timeline, resource allocation, and success metrics. Strategy is regularly reviewed and adjusted."
      },
      {
        value: 5,
        label: "Advanced strategy fully integrated with business",
        description: "AI strategy is seamlessly integrated with overall business strategy. Includes comprehensive roadmaps, governance frameworks, and is a key driver of business transformation."
      }
    ]
  },
  {
    id: "s2",
    text: "How frequently does leadership discuss AI adoption and impact?",
    dimension: "strategy",
    tooltip: "Assess how often and at what level AI topics are discussed in leadership meetings. Consider formal board discussions, executive team meetings, and how regularly AI appears on leadership agendas.",
    options: [
      {
        value: 0,
        label: "No structured AI discussions occur at leadership level",
        description: "Leadership does not engage in formal discussions about AI. Any AI-related conversations are ad hoc and not part of structured leadership agenda items."
      },
      {
        value: 1,
        label: "Annual strategic review with 1-2 dedicated sessions",
        description: "AI appears in formal leadership discussions only during annual strategic reviews, with minimal time allocated and limited follow-up actions."
      },
      {
        value: 2,
        label: "Quarterly leadership discussions on AI progress",
        description: "Leadership reviews AI initiatives on a quarterly basis with moderate engagement. Discussions focus on high-level progress reports with limited strategic direction."
      },
      {
        value: 3,
        label: "Monthly executive meetings with AI agenda items",
        description: "Monthly leadership meetings include dedicated AI agenda items. Discussions cover implementation progress, challenges, and adjustments to strategic direction."
      },
      {
        value: 4,
        label: "Weekly leadership engagement with AI initiatives",
        description: "Leadership engages with AI topics in weekly meetings. Key executives actively monitor progress, provide direction, and ensure alignment with business goals."
      },
      {
        value: 5,
        label: "Continuous executive involvement in AI strategy",
        description: "Leadership maintains continuous engagement with AI initiatives. Executives champion AI adoption, regularly participate in implementation reviews, and actively shape strategic direction."
      }
    ]
  },
  {
    id: "s3",
    text: "What percentage of your organization's budget is allocated to AI?",
    dimension: "strategy",
    tooltip: "Evaluate the financial commitment to AI initiatives as a percentage of your total organizational or IT budget. Include direct AI project costs, talent, infrastructure, and related investments.",
    options: [
      {
        value: 0,
        label: "No specific AI budget allocation",
        description: "AI initiatives are funded on an ad-hoc basis without dedicated budget lines. Projects compete for general IT or innovation funding with no protected AI allocation."
      },
      {
        value: 1,
        label: "Minimal budget (<1% of IT spend)",
        description: "A small experimental budget exists primarily for proofs of concept and limited trials. Funding is typically year-to-year with no long-term commitment."
      },
      {
        value: 2,
        label: "Small allocation (1-3% of IT budget)",
        description: "Dedicated line item for AI exists within IT budget. Funding is sufficient for several targeted projects but still represents a minor part of technology investment."
      },
      {
        value: 3,
        label: "Moderate investment (4-6% of IT budget)",
        description: "Substantial AI budget enables multiple concurrent projects across several business units. Multi-year funding commitments support sustained development efforts."
      },
      {
        value: 4,
        label: "Significant funding (7-10% of IT budget)",
        description: "AI receives priority funding status with dedicated resources for infrastructure, talent, and projects. Budget allows for advanced deployments and specialized expertise."
      },
      {
        value: 5,
        label: "Strategic priority (>12% of total IT budget)",
        description: "AI is considered a core business capability with extensive funding across infrastructure, research, deployment, and talent acquisition. Budget includes provisions for emerging technologies, partnerships, and long-term research initiatives."
      }
    ]
  },
  {
    id: "s4",
    text: "How is AI governance structured in your organization?",
    dimension: "strategy",
    tooltip: "Assess the formality and maturity of your AI governance structure. Look for defined roles, committees, policies, and decision-making frameworks specifically focused on AI oversight.",
    options: [
      {
        value: 0,
        label: "No AI governance structure exists",
        description: "The organization has no defined roles, committees, or policies specifically for AI governance. Decisions about AI are made inconsistently without formal oversight."
      },
      {
        value: 1,
        label: "Informal oversight by IT leadership",
        description: "IT leadership provides basic oversight of AI initiatives as part of general technology governance. No dedicated AI governance policies or committees exist."
      },
      {
        value: 2,
        label: "Basic AI steering committee",
        description: "A designated committee with limited authority provides preliminary guidance for AI initiatives. Meets occasionally with emerging but incomplete governance policies."
      },
      {
        value: 3,
        label: "Formal cross-functional governance body",
        description: "Established governance structure with representation from multiple departments. Regular meetings with documented policies and defined decision-making authority."
      },
      {
        value: 4,
        label: "Mature governance with executive sponsors",
        description: "Comprehensive governance framework with executive sponsorship and clear accountability. Includes specialized subcommittees for technical, ethical, and strategic oversight."
      },
      {
        value: 5,
        label: "Enterprise-wide AI governance framework",
        description: "Sophisticated governance system fully integrated with enterprise risk management. Includes board-level oversight, dedicated AI ethics committee, and comprehensive policy framework."
      }
    ]
  },
  {
    id: "s5",
    text: "How does your organization address ethical AI considerations?",
    dimension: "strategy",
    tooltip: "Evaluate your organization's approach to AI ethics, including formal policies, review processes, and guidelines for responsible AI development and deployment.",
    options: [
      {
        value: 0,
        label: "No formal ethical AI considerations",
        description: "Ethical aspects of AI are not formally considered in development or deployment. No documented principles or review processes exist."
      },
      {
        value: 1,
        label: "Basic awareness of ethical issues",
        description: "Some recognition of ethical considerations exists, but without formal policies. Discussions occur sporadically with minimal documentation or guidelines."
      },
      {
        value: 2,
        label: "Informal guidelines and discussions",
        description: "Basic ethical guidelines exist but are not consistently enforced. Occasional team discussions address ethical considerations for major projects."
      },
      {
        value: 3,
        label: "Documented ethical AI principles",
        description: "Formal ethical AI principles are documented and communicated organization-wide. Guidelines are referenced during project development but lack structured review."
      },
      {
        value: 4,
        label: "Ethics review process for AI projects",
        description: "Systematic ethics review process for all AI initiatives with dedicated personnel. Includes formal framework for evaluating bias, fairness, and transparency."
      },
      {
        value: 5,
        label: "Comprehensive ethical AI framework with oversight",
        description: "Advanced ethics governance with dedicated committee, external audits, and continuous monitoring. Ethical considerations deeply integrated into all phases of AI development."
      }
    ]
  },
  {
    id: "s6",
    text: "How is your AI strategy aligned with business objectives?",
    dimension: "strategy",
    tooltip: "Assess how directly your AI initiatives connect to and support specific business goals. Consider if AI projects are explicitly linked to KPIs, revenue targets, or strategic business outcomes.",
    options: [
      {
        value: 0,
        label: "No explicit alignment with business goals",
        description: "AI initiatives are developed without clear connection to business objectives. Projects are technology-driven rather than business-outcome focused."
      },
      {
        value: 1,
        label: "General awareness of business relevance",
        description: "Basic understanding that AI should support business goals exists, but specific connections between initiatives and objectives are rarely documented."
      },
      {
        value: 2,
        label: "Some projects tied to business outcomes",
        description: "Select AI projects (25-40%) have documented links to business KPIs. Others remain primarily technology experiments with indirect business relevance."
      },
      {
        value: 3,
        label: "Most AI initiatives support business objectives",
        description: "Majority of AI projects (60-75%) have clear business objectives with defined success metrics. Regular reviews ensure continued alignment."
      },
      {
        value: 4,
        label: "Direct alignment with strategic priorities",
        description: "Nearly all AI initiatives (85-95%) directly support strategic business priorities. Projects have documented KPIs that connect to organizational goals."
      },
      {
        value: 5,
        label: "AI fully integrated into business value creation",
        description: "AI strategy is inseparable from business strategy. All initiatives directly support value creation with measurable impact on core business objectives and competitive advantage."
      }
    ]
  },
  {
    id: "s7",
    text: "Who leads AI initiatives in your organization?",
    dimension: "strategy",
    tooltip: "Identify the leadership and reporting structure for AI initiatives. Consider the seniority, authority, and focus of those responsible for driving AI strategy and implementation.",
    options: [
      {
        value: 0,
        label: "No designated AI leadership",
        description: "No specific individual or team is formally responsible for AI initiatives. AI-related efforts are ad hoc and without consistent leadership."
      },
      {
        value: 1,
        label: "IT manager with partial AI responsibilities",
        description: "An IT manager oversees AI initiatives as one of many responsibilities. Lacks dedicated focus, specialized expertise, or strategic authority."
      },
      {
        value: 2,
        label: "Dedicated AI/ML team lead",
        description: "A specialized team lead manages AI projects but operates at middle management level. Limited authority for strategic decisions and resource allocation."
      },
      {
        value: 3,
        label: "Director or VP of AI/Data",
        description: "Senior leadership role focused on AI/data with significant decision authority. Reports to C-suite with dedicated team and formal mandate."
      },
      {
        value: 4,
        label: "C-suite executive (CIO/CTO/CDO) with AI focus",
        description: "C-level executive takes direct responsibility for AI strategy with enterprise-wide authority. AI is a primary focus area within their broader technology mandate."
      },
      {
        value: 5,
        label: "Chief AI Officer or dedicated AI executive",
        description: "Specialized C-suite position focused exclusively on AI strategy and execution. Has direct board access, dedicated resources, and organization-wide authority."
      }
    ]
  },
  {
    id: "s8",
    text: "How is AI success measured in your organization?",
    dimension: "strategy",
    tooltip: "Evaluate the metrics and evaluation frameworks used to measure the success of AI initiatives. Consider specificity, comprehensiveness, and integration with overall business metrics.",
    options: [
      {
        value: 0,
        label: "No formal success metrics for AI",
        description: "Success of AI initiatives is not formally measured. Evaluations, if any, are subjective without defined metrics or tracking procedures."
      },
      {
        value: 1,
        label: "Basic technical metrics (accuracy, performance)",
        description: "Success evaluation focuses solely on basic technical metrics like model accuracy or system performance. Business impact is not systematically measured."
      },
      {
        value: 2,
        label: "Project completion and delivery metrics",
        description: "Metrics center on project delivery (timelines, budget adherence) with limited focus on post-implementation impact or business value."
      },
      {
        value: 3,
        label: "Business impact measured for key projects",
        description: "Major AI projects (50-70%) include business impact metrics. Evaluation includes both technical performance and contribution to business objectives."
      },
      {
        value: 4,
        label: "Comprehensive ROI and value tracking",
        description: "Formal ROI framework applied to all significant AI initiatives. Includes detailed cost-benefit analysis and quantitative measures of business impact."
      },
      {
        value: 5,
        label: "Advanced metrics with business transformation KPIs",
        description: "Sophisticated measurement framework connects AI initiatives to strategic business transformation. Includes leading and lagging indicators with continuous monitoring."
      }
    ]
  },
  {
    id: "s9",
    text: "Does your organization have a roadmap for AI implementation?",
    dimension: "strategy",
    tooltip: "Assess the clarity, detail, and timeframe of your organization's AI implementation plans. Look for documented stages, milestones, and long-term vision for AI adoption.",
    options: [
      {
        value: 0,
        label: "No AI roadmap exists",
        description: "No documented plan for AI implementation exists. AI initiatives happen opportunistically without coordinated planning or sequencing."
      },
      {
        value: 1,
        label: "Informal ideas for future AI adoption",
        description: "General concepts for AI use exist but without formal documentation. Ideas lack specificity, timelines, or resource allocation plans."
      },
      {
        value: 2,
        label: "Basic 1-year implementation plan",
        description: "Short-term roadmap outlines AI initiatives for the next year. Limited detail on dependencies, resources, or strategic progression beyond immediate projects."
      },
      {
        value: 3,
        label: "Detailed 1-3 year AI roadmap",
        description: "Medium-term roadmap with clear milestones, resource requirements, and expected outcomes. Includes contingency planning and periodic review points."
      },
      {
        value: 4,
        label: "Comprehensive 3-5 year strategic plan",
        description: "Long-term strategic roadmap with detailed implementation stages. Includes technology evolution, talent development, and integration with broader digital transformation."
      },
      {
        value: 5,
        label: "Long-term vision with rolling implementation plan",
        description: "Visionary long-term strategy with continuously updated implementation roadmap. Balances strategic direction with agility to incorporate emerging technologies and market changes."
      }
    ]
  },
  {
    id: "s10",
    text: "How is AI positioned within your organization's strategy?",
    dimension: "strategy",
    tooltip: "Consider how AI is framed in your overall business strategy. Evaluate whether it's viewed as a tactical tool, a strategic advantage, or a core business transformation element.",
    options: [
      {
        value: 0,
        label: "AI not mentioned in organizational strategy",
        description: "AI is absent from formal strategy documents and leadership communications. No strategic positioning or role defined for AI technologies."
      },
      {
        value: 1,
        label: "AI viewed as experimental technology",
        description: "AI is mentioned as an emerging technology to monitor and experiment with. Positioned as optional and separate from core business operations."
      },
      {
        value: 2,
        label: "AI recognized as operational efficiency tool",
        description: "AI is primarily positioned as a tool for cost reduction and operational improvements. Focus is tactical rather than strategic transformation."
      },
      {
        value: 3,
        label: "AI positioned as strategic capability",
        description: "AI is recognized as a strategic capability that can provide competitive advantages. Included in strategic planning with specific objectives."
      },
      {
        value: 4,
        label: "AI central to strategic competitive advantage",
        description: "AI is positioned as a core differentiator and competitive advantage. Features prominently in business strategy and executive communications."
      },
      {
        value: 5,
        label: "AI as core business transformation driver",
        description: "AI is positioned as a fundamental transformation driver reshaping the business model and market approach. Central to the organization's future vision and identity."
      }
    ]
  }
];
